import { Dialog, IconButton } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import { TbCirclePlus } from "react-icons/tb";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../../Hooks/toast";
import { fetchBaasApiAccessInfo } from "../../../../../Services/baasApiApi";
import {
  createBaasAccount,
  fetchBaasAccounts,
  getBaasAccountCreatableCurrencies,
  getBaasAccountEntities,
  getEntityBlockedCurrencies,
} from "../../../../../Services/baasAccountsListApi";
import { formatBaasConsoleConflictMessage } from "../../../../../Services/baasConsoleClient";
import { formatBaasEntityValidationMessage } from "../../../../../Services/baasEntityValidationMessage";

const MODAL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-lg";

/** Contas "Criar conta" always opens a person/business banking rail (not WALLET/OTC). */
const CREATE_ACCOUNT_TYPE = "operational";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const chipClass = (selected, disabled) =>
  [
    "inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-semibold transition",
    disabled ? "cursor-not-allowed opacity-45" : "",
    selected
      ? "border-[#e7a73f] bg-[#e7a73f]/15 text-[#b87a1f] dark:text-[#e7a73f]"
      : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-300",
  ]
    .filter(Boolean)
    .join(" ");

const entityMatchesSearch = (entity, query) => {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  const haystack = [entity?.name, entity?.document, entity?.id]
    .map((value) => String(value || "").toLowerCase())
    .join(" ");
  return haystack.includes(q);
};

const isCurrencyAlreadyOpenError = (message) =>
  /already has an active [A-Z]{2}\b/i.test(message) ||
  /already has banking data for country/i.test(message) ||
  /already has a balance for currency/i.test(message) ||
  /active [A-Z]{2} bank account/i.test(message);

export const BaasAccountCreateModal = ({
  open,
  onClose,
  initialEntityId = "",
  onCreated,
}) => {
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();

  const [entities, setEntities] = useState([]);
  const [entitySearch, setEntitySearch] = useState("");
  const [entityId, setEntityId] = useState("");
  const [currencies, setCurrencies] = useState(["BRL"]);
  const [availableCurrencies, setAvailableCurrencies] = useState(["BRL", "USD"]);
  const [alias, setAlias] = useState("");
  const [loading, setLoading] = useState(false);
  const [entityAccounts, setEntityAccounts] = useState([]);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!open || !selectedInstance) {
      setEntities([]);
      setEntityAccounts([]);
      setAvailableCurrencies(["BRL", "USD"]);
      return undefined;
    }

    (async () => {
      try {
        const [nextEntities, accessInfo, accounts] = await Promise.all([
          getBaasAccountEntities(selectedInstance),
          fetchBaasApiAccessInfo(selectedInstance).catch(() => null),
          fetchBaasAccounts(selectedInstance, { includeBalances: true }).catch(() => []),
        ]);
        if (cancelled) return;

        setEntities(nextEntities);
        setEntityAccounts(Array.isArray(accounts) ? accounts : []);

        const instanceWithCurrencies = {
          ...selectedInstance,
          supportedCurrencies:
            accessInfo?.supportedCurrencies?.length > 0
              ? accessInfo.supportedCurrencies
              : selectedInstance?.supportedCurrencies,
        };
        const nextCurrencies = getBaasAccountCreatableCurrencies(instanceWithCurrencies);
        setAvailableCurrencies(nextCurrencies);
      } catch {
        if (!cancelled) {
          setEntities([]);
          setEntityAccounts([]);
          setAvailableCurrencies(["BRL", "USD"]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedInstance]);

  useEffect(() => {
    if (!open) {
      setEntityId("");
      setEntitySearch("");
      setCurrencies(["BRL"]);
      setAlias("");
      setLoading(false);
      setFormError(null);
      return;
    }

    const preferred =
      (initialEntityId && entities.some((entity) => entity.id === initialEntityId)
        ? initialEntityId
        : "") ||
      entities[0]?.id ||
      "";
    setEntityId(preferred);
    setEntitySearch("");
    setAlias("");
    setLoading(false);
    setFormError(null);
  }, [open, initialEntityId, entities]);

  const blockedCurrencies = useMemo(
    () => getEntityBlockedCurrencies(entityAccounts, entityId, availableCurrencies),
    [entityAccounts, entityId, availableCurrencies],
  );

  const blockedSet = useMemo(() => new Set(blockedCurrencies), [blockedCurrencies]);

  const selectableCurrencies = useMemo(
    () => availableCurrencies.filter((code) => !blockedSet.has(code)),
    [availableCurrencies, blockedSet],
  );

  useEffect(() => {
    if (!open) return;
    setCurrencies((current) => {
      const stillValid = current.filter((code) => selectableCurrencies.includes(code));
      if (stillValid.length === 1) return stillValid;
      if (stillValid.length > 1) return [stillValid[0]];
      return selectableCurrencies.length > 0 ? [selectableCurrencies[0]] : [];
    });
  }, [open, entityId, selectableCurrencies]);

  const filteredEntities = useMemo(
    () => entities.filter((entity) => entityMatchesSearch(entity, entitySearch)),
    [entities, entitySearch],
  );

  const selectedEntity = useMemo(
    () => entities.find((entity) => entity.id === entityId) || null,
    [entities, entityId],
  );

  const toggleCurrency = (code) => {
    if (blockedSet.has(code)) return;

    setCurrencies((current) => {
      if (current.includes(code)) {
        if (current.length === 1) return current;
        return current.filter((item) => item !== code);
      }
      // Single country/currency open per call — no multi-open yet.
      return [code];
    });
  };

  const noCurrencyAvailable = selectableCurrencies.length === 0;
  const hasBlockedCurrency = blockedCurrencies.length > 0;

  const canSubmit =
    Boolean(entityId) &&
    currencies.length > 0 &&
    currencies.every((code) => selectableCurrencies.includes(code)) &&
    !noCurrencyAvailable &&
    !loading;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || !selectedInstance?.baasInstanceId) return;

    const blockedSelection = currencies.find((code) => blockedSet.has(code));
    if (blockedSelection) {
      setFormError(
        intl.get("baas.accounts.create.currencyBlocked", {
          currency: blockedSelection,
        }),
      );
      return;
    }

    setLoading(true);
    setFormError(null);
    try {
      const entityName = selectedEntity?.name || "";
      const account = await createBaasAccount(selectedInstance, {
        entityId,
        entityName,
        type: CREATE_ACCOUNT_TYPE,
        currencies,
        alias,
        is_root: false,
        pinned: false,
      });

      if (!account) {
        setFormError(intl.get("baas.accounts.create.error"));
        return;
      }

      addToast({
        type: "success",
        title: intl.get("baas.accounts.create.success"),
      });
      onCreated?.(account);
      onClose?.();
    } catch (error) {
      const message = String(error?.message || error?.body?.message || "");
      const currencyMatch = message.match(/currency ['"]?([A-Z]{3})/i);
      const countryMatch = message.match(
        /(?:country|active)\s+['"]?([A-Z]{2})\b/i,
      );
      const blockedCode =
        currencyMatch?.[1]?.toUpperCase() ||
        (countryMatch?.[1]
          ? { BR: "BRL", US: "USD", GB: "GBP", EU: "EUR" }[countryMatch[1].toUpperCase()]
          : null) ||
        currencies[0];

      const validationMessage = formatBaasEntityValidationMessage(error);
      setFormError(
        isCurrencyAlreadyOpenError(message)
          ? intl.get("baas.accounts.create.currencyBlocked", {
              currency: blockedCode || "—",
            })
          : formatBaasConsoleConflictMessage(error)
            || validationMessage
            || intl.get("baas.accounts.create.error"),
      );
    } finally {
      setLoading(false);
    }
  };

  const currencyHint = noCurrencyAvailable
    ? intl.get("baas.accounts.create.noCurrencyAvailable")
    : hasBlockedCurrency
      ? intl.get("baas.accounts.create.currencyBlockedHint")
      : intl.get("baas.accounts.create.currencyHint");

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: MODAL_CLASS }}
    >
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-brand-dark-border sm:px-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <TbCirclePlus className="h-5 w-5 shrink-0 text-[#e7a73f]" aria-hidden />
              <h2 className="text-lg font-bold text-brand-dark dark:text-gray-100">
                {intl.get("baas.accounts.create.pageTitle")}
              </h2>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.accounts.create.subtitle")}
            </p>
          </div>
          <IconButton
            type="button"
            size="small"
            onClick={onClose}
            disabled={loading}
            aria-label={intl.get("baas.accounts.create.cancel")}
          >
            <MdClose />
          </IconButton>
        </div>

        <div className="space-y-4 px-5 py-5 sm:px-6">
          {formError ? (
            <p
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
            >
              {formError}
            </p>
          ) : null}
          <div>
            <label
              htmlFor="baas-account-create-entity-search"
              className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              {intl.get("baas.accounts.create.entityLabel")}
            </label>
            <input
              id="baas-account-create-entity-search"
              type="search"
              className={inputClass}
              value={entitySearch}
              onChange={(event) => setEntitySearch(event.target.value)}
              placeholder={intl.get("baas.accounts.create.entitySearchPlaceholder")}
              autoComplete="off"
            />
            <select
              id="baas-account-create-entity"
              className={`${inputClass} mt-2`}
              value={entityId}
              onChange={(event) => setEntityId(event.target.value)}
              required
            >
              <option value="">{intl.get("baas.accounts.create.entityPlaceholder")}</option>
              {filteredEntities.map((entity) => (
                <option key={entity.id} value={entity.id}>
                  {entity.document
                    ? `${entity.name} · ${entity.document}`
                    : entity.name}
                </option>
              ))}
            </select>
            {entitySearch.trim() && filteredEntities.length === 0 ? (
              <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                {intl.get("baas.accounts.create.entitySearchEmpty")}
              </p>
            ) : null}
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.accounts.create.currencyLabel")}
            </p>
            <div className="flex flex-wrap gap-2">
              {availableCurrencies.map((code) => {
                const selected = currencies.includes(code);
                const disabled = blockedSet.has(code);
                return (
                  <button
                    key={code}
                    type="button"
                    className={chipClass(selected, disabled)}
                    aria-pressed={selected}
                    disabled={disabled}
                    title={
                      disabled
                        ? intl.get("baas.accounts.create.currencyBlocked", {
                            currency: code,
                          })
                        : undefined
                    }
                    onClick={() => toggleCurrency(code)}
                  >
                    {code}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
              {currencyHint}
            </p>
          </div>

          <div>
            <label
              htmlFor="baas-account-create-alias"
              className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              {intl.get("baas.accounts.create.aliasLabel")}
            </label>
            <input
              id="baas-account-create-alias"
              type="text"
              className={inputClass}
              value={alias}
              onChange={(event) => setAlias(event.target.value)}
              placeholder={intl.get("baas.accounts.create.aliasPlaceholder")}
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 px-5 py-4 dark:border-brand-dark-border sm:px-6">
          <button
            type="button"
            className="btn-outline w-auto"
            onClick={onClose}
            disabled={loading}
          >
            {intl.get("baas.accounts.create.cancel")}
          </button>
          <button type="submit" className="btn-primary w-auto" disabled={!canSubmit}>
            {loading
              ? intl.get("baas.accounts.create.submitting")
              : intl.get("baas.accounts.create.submit")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default BaasAccountCreateModal;
